/**
 * @file リーダーのしぼりこみの選択肢を作る (純関数)。
 *
 * @what: 取り込んだ問から、単元と出典校の選択肢を件数つきで組み立てる。
 * @why: 単元は到達度モデルの単元名で出す。計画の面で見た言葉のまま探せるようにするため
 *   (代表 2026-08-22)。モデルに無い語彙 (取り込み側が先に増えた語) を落とすと、その問へ
 *   単元から辿り着けなくなるので `分野/単元` のまま残す。
 * @failure: 例外を投げない。`分野`・`単元`・`出典校` が無い問はその軸の選択肢に載らない
 *   (= その軸からは引けないが、しぼりこみ無しでは読める)。
 */

import type { StudyScanItem } from "../api/scans";
import { taxonomyKey } from "./corpusIndex";
import type { AttainmentModel } from "./types";

/** しぼりこみの選択肢 1 つ。 */
export interface FilterOption {
  value: string;
  label: string;
  count: number;
}

/**
 * 単元の選択肢と、選択肢 → その問の id の対応。
 *
 * value は `u:{unit.id}` (モデルの単元) か `t:{分野/単元}` (モデルに無い語彙)。計画の面から
 * 渡ってくる `?unit=` はモデルの単元 id なので、そのまま `u:` 側と噛み合う。
 */
export function buildUnitOptions(
  model: AttainmentModel,
  items: readonly StudyScanItem[],
): { options: FilterOption[]; match: Map<string, ReadonlySet<string>> } {
  const keyToUnit = new Map<string, { id: string; label: string }>();
  for (const u of model.units) {
    for (const key of u.taxonomy) {
      if (!keyToUnit.has(key)) keyToUnit.set(key, { id: u.id, label: u.label });
    }
  }
  const buckets = new Map<string, { label: string; ids: Set<string> }>();
  for (const item of items) {
    const key = taxonomyKey(item.index);
    if (!key) continue;
    const unit = keyToUnit.get(key);
    const value = unit ? `u:${unit.id}` : `t:${key}`;
    const bucket = buckets.get(value) ?? { label: unit ? unit.label : key, ids: new Set<string>() };
    bucket.ids.add(item.id);
    buckets.set(value, bucket);
  }
  const options = [...buckets.entries()]
    .map(([value, b]) => ({ value, label: b.label, count: b.ids.size }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ja"));
  const match = new Map<string, ReadonlySet<string>>(
    [...buckets.entries()].map(([value, b]) => [value, b.ids]),
  );
  return { options, match };
}

/** 出典校の選択肢 (件数の多い順)。 */
export function buildSchoolOptions(items: readonly StudyScanItem[]): FilterOption[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const school = item.index["出典校"]?.trim();
    if (!school) continue;
    counts.set(school, (counts.get(school) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ja"));
}
